/**
 * Sound Manager for Chess Application
 * Handles all audio playback for game events
 */

export type SoundType = 'move' | 'capture' | 'check' | 'checkmate' | 'castle' | 'promote' | 'draw';

/**
 * Sound manager class to handle all game sounds
 */
class SoundManager {
  private enabled: boolean;
  private volume: number; // Volume level (0.0 to 1.0)
  private audioContext: AudioContext | null = null;

  constructor() {
    // Load sound preference from localStorage (default: enabled)
    this.enabled = localStorage.getItem('chess-sounds-enabled') !== 'false';
    
    // Load volume preference from localStorage (default: 0.5)
    const savedVolume = localStorage.getItem('chess-sounds-volume');
    this.volume = savedVolume !== null ? parseFloat(savedVolume) : 0.5;
  }

  /**
   * Initialize audio context (required for Web Audio API)
   */
  private initAudioContext(): void {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
  }

  /**
   * Play a synthesized sound for the given event type
   */
  play(soundType: SoundType): void {
    if (!this.enabled || this.volume === 0) {
      return;
    }

    this.initAudioContext();
    if (!this.audioContext) {
      return;
    }

    const ctx = this.audioContext;
    const now = ctx.currentTime;

    // Create oscillator and gain nodes
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    // Connect nodes
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    // Base gain values (will be multiplied by volume)
    let baseGain = 0.15;

    // Configure sound based on type
    switch (soundType) {
      case 'move':
        // Simple click sound - short, mid-frequency
        oscillator.frequency.setValueAtTime(440, now); // A4
        baseGain = 0.15;
        gainNode.gain.setValueAtTime(baseGain * this.volume, now);
        gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
        oscillator.start(now);
        oscillator.stop(now + 0.08);
        break;

      case 'capture':
        // Sharper, higher sound for captures
        oscillator.frequency.setValueAtTime(600, now); // D5
        baseGain = 0.2;
        gainNode.gain.setValueAtTime(baseGain * this.volume, now);
        gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
        oscillator.start(now);
        oscillator.stop(now + 0.12);
        break;

      case 'check':
        // Warning sound - two-tone
        oscillator.frequency.setValueAtTime(700, now); // F5
        oscillator.frequency.setValueAtTime(800, now + 0.05); // G#5
        baseGain = 0.25;
        gainNode.gain.setValueAtTime(baseGain * this.volume, now);
        gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
        oscillator.start(now);
        oscillator.stop(now + 0.15);
        break;

      case 'checkmate':
        // Dramatic descending sound
        oscillator.frequency.setValueAtTime(880, now); // A5
        oscillator.frequency.exponentialRampToValueAtTime(440, now + 0.3); // A4
        baseGain = 0.3;
        gainNode.gain.setValueAtTime(baseGain * this.volume, now);
        gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
        oscillator.start(now);
        oscillator.stop(now + 0.35);
        break;

      case 'castle':
        // Special sound - rising tone
        oscillator.frequency.setValueAtTime(350, now); // F4
        oscillator.frequency.exponentialRampToValueAtTime(550, now + 0.15); // C#5
        baseGain = 0.18;
        gainNode.gain.setValueAtTime(baseGain * this.volume, now);
        gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.18);
        oscillator.start(now);
        oscillator.stop(now + 0.18);
        break;

      case 'promote':
        // Triumphant sound - ascending
        oscillator.frequency.setValueAtTime(440, now); // A4
        oscillator.frequency.exponentialRampToValueAtTime(880, now + 0.2); // A5
        baseGain = 0.2;
        gainNode.gain.setValueAtTime(baseGain * this.volume, now);
        gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
        oscillator.start(now);
        oscillator.stop(now + 0.25);
        break;

      case 'draw':
        // Neutral sound - single tone
        oscillator.frequency.setValueAtTime(500, now); // B4
        baseGain = 0.2;
        gainNode.gain.setValueAtTime(baseGain * this.volume, now);
        gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
        oscillator.start(now);
        oscillator.stop(now + 0.25);
        break;
    }
  }

  /**
   * Enable sound effects
   */
  enable(): void {
    this.enabled = true;
    localStorage.setItem('chess-sounds-enabled', 'true');
  }

  /**
   * Disable sound effects
   */
  disable(): void {
    this.enabled = false;
    localStorage.setItem('chess-sounds-enabled', 'false');
  }

  /**
   * Toggle sound effects on/off
   */
  toggle(): boolean {
    this.enabled = !this.enabled;
    localStorage.setItem('chess-sounds-enabled', this.enabled.toString());
    return this.enabled;
  }

  /**
   * Check if sounds are currently enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Set volume level (0.0 to 1.0)
   */
  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume)); // Clamp between 0 and 1
    localStorage.setItem('chess-sounds-volume', this.volume.toString());
  }

  /**
   * Get current volume level (0.0 to 1.0)
   */
  getVolume(): number {
    return this.volume;
  }
}

// Export singleton instance
export const soundManager = new SoundManager();
